import { useExpandHeight } from "@redotech/react-animation/transition";
import { useHandler } from "@redotech/react-util/hook";
import ChevronDownFilledIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import FileIcon from "@redotech/redo-icons/file-06.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

export interface AiSource {
  id: string;
  name: string;
  url?: string;
}

function SourcesList({ sources }: { sources: AiSource[] }) {
  return (
    <Flex align="flex-start" dir="column" gap="xs">
      {sources.map((source) =>
        source.url ? (
          <a
            href={source.url}
            key={source.id}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Flex align="center" gap="sm">
              <FileIcon className="size-3 shrink-0" />
              <Text fontSize="xs" fontWeight="regular" textColor="primary">
                {source.name}
              </Text>
            </Flex>
          </a>
        ) : (
          <Text
            fontSize="xs"
            fontWeight="regular"
            key={source.id}
            textColor="primary"
          >
            {source.name}
          </Text>
        ),
      )}
    </Flex>
  );
}

export const AiSources = memo(function AiSources({
  sources,
}: {
  sources: AiSource[];
}) {
  const [open, setOpen] = useState(false);
  const [anchorRef, setAnchorRef] = useState<HTMLButtonElement | null>(null);

  const items: RedoCommandMenuItem[] = useMemo(
    () =>
      sources.map((source) => ({
        Icon: FileIcon,
        text: source.name,
        onClick: () => {
          if (source.url) {
            window.open(source.url, "_blank", "noopener,noreferrer");
          }
        },
      })),
    [sources],
  );

  if (sources.length === 0) {
    return null;
  }

  return (
    <>
      <RedoButton
        hierarchy="tertiary"
        IconTrailing={ChevronDownFilledIcon}
        onClick={() => setOpen((prev) => !prev)}
        ref={setAnchorRef}
        size="xs"
        text={`Sources (${sources.length})`}
      />
      <RedoCommandMenu
        anchor={anchorRef}
        items={items}
        open={open}
        placement="bottom-start"
        setOpen={setOpen}
      />
    </>
  );
});

export const AiSourcesInline = memo(function AiSourcesInline({
  sources,
}: {
  sources: AiSource[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [animateStyles, animating] = useExpandHeight(expanded, element);
  const toggle = useHandler(() => setExpanded((v) => !v));

  if (sources.length === 0) {
    return null;
  }

  return (
    <Flex align="flex-start" dir="column" gap="none">
      <Flex align="center" className="cursor-pointer" gap="xs" onClick={toggle}>
        <ChevronDownFilledIcon
          className={`size-3 shrink-0 transition-transform duration-200 ${expanded ? "" : "-rotate-90"}`}
        />
        <Text fontSize="xs" fontWeight="regular" textColor="secondary">
          {`Used ${sources.length} source${sources.length !== 1 ? "s" : ""}`}
        </Text>
      </Flex>
      <Flex
        className="overflow-hidden transition-[height] duration-200 ease-in-out"
        dir="column"
        ref={setElement}
        style={animateStyles}
      >
        {(animating || expanded) && (
          <Flex dir="column" pt="md">
            <SourcesList sources={sources} />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});
